import React, { useState, useEffect } from 'react'
import { ClockIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../hooks/useTranslation'
import { getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import { itemTypeIconMap } from '../../ActionPlan/TrackTileComponents/ActiveTile'
import Spinner from '~/components/shared/Spinner'
import { isEmpty } from 'lodash'

export function RecommendedCard({ className }) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [recommendations, setRecommendations] = useState([])

  useEffect(() => {
    setIsLoading(true)
    // eslint-disable-next-line promise/catch-or-return
    getApi()
      .get('/tracker/get_tracker_sections')
      .then((response) => {
        if (response.status == 200) {
          // Extract all subsections from all tracker sections
          const allSubsections = response.data.trackerSections.reduce(
            (acc, section) => {
              const sectionData = Object.values(section)[0]
              return [...acc, ...sectionData.subsections]
            },
            [],
          )

          // Filter for uncompleted items and take first 2
          const uncompletedItems = allSubsections
            .filter((item) => !item.completed && item.type !== 'insights')
            .slice(0, 2)

          setRecommendations(uncompletedItems)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .catch((error) => {
        console.log('json request failed', error)
        setRecommendations([])
        return
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [setRecommendations])

  if (isEmpty(recommendations)) {
    return null
  }

  return (
    <Stack className={className} spacing={3}>
      <Typography variant="title4" color="primary" component="h2">
        {t('recommended_card.title')}
      </Typography>
      <Typography>{t('recommended_card.description')}</Typography>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner show className="h-9 w-9" />
        </div>
      ) : (
        <Stack spacing={4}>
          <div className="grid grid-cols-[1fr] gap-8 lg:grid-cols-[1fr_1fr]">
            {recommendations.map((recommendation, index) => {
              return (
                <Card
                  key={index}
                  title={recommendation.name}
                  titleColor="neutral"
                  image={
                    recommendation.image?.path ||
                    'https://www-yns-com.s3.us-east-2.amazonaws.com/images/orientation/landing_bg.jpg'
                  }
                  imageAlt={recommendation.image?.title || ''}
                  imageBadge={
                    !!recommendation.length && (
                      <span className="flex flex-row items-center">
                        {recommendation.length}
                        <ClockIcon className="h-7 w-7 pl-2 text-white" />
                      </span>
                    )
                  }
                  actions={
                    <Button
                      href={
                        recommendation.url ||
                        `/orientation/resources/${recommendation.type}/${recommendation.slug}`
                      }
                      fullWidth
                      endIcon={
                        itemTypeIconMap[recommendation.type] && (
                          <>
                            {React.cloneElement(
                              itemTypeIconMap[recommendation.type],
                              { height: 24 },
                            )}
                          </>
                        )
                      }
                    >
                      {t(`item_type_actions.${recommendation.type}`)}
                    </Button>
                  }
                >
                  <Typography>{recommendation.description}</Typography>
                </Card>
              )
            })}
          </div>
        </Stack>
      )}
    </Stack>
  )
}
